import { useState } from 'react';
import type { BadgeVariant } from '@/components/ui/Badge';
import { MarkdownContent } from '@/components/ui';
import { isJsonLike } from '../../utils/format';
import { cleanDisplayText } from '@/utils/display-text';
import type {
  RunCompletedData, RunFailedData, RunReasoningDeltaData, RunResponseDeltaData,
  RunResponseStopData, RunFinalAnswerDeltaData, RunFinalAnswerStopData,
  RunToolCallData, RunToolResultData,
  RunSubagentInvokedData, RunSubagentReturnedData, RunSubagentStartedData,
  RunSubagentCompletedData, RunSubagentsAllReturnedData,
  KBDocumentAddedData, KBSearchContentAddedData, KBProcessingCompletedData,
  EmailProcessingStartedData, EmailProcessingCompletedData, EmailProcessingFailedData,
} from '../../types/events';

const eventBadgeMap: Record<string, BadgeVariant> = {
  'agent.run.started': 'info',
  'agent.run.reasoning_start': 'purple',
  'agent.run.reasoning_delta': 'purple',
  'agent.run.reasoning_stop': 'purple',
  'agent.run.response_start': 'gray',
  'agent.run.response_delta': 'gray',
  'agent.run.response_stop': 'gray',
  'agent.run.final_answer_start': 'success',
  'agent.run.final_answer_delta': 'success',
  'agent.run.final_answer_stop': 'success',
  'agent.run.tool_call': 'teal',
  'agent.run.tool_result': 'teal',
  'agent.run.completed': 'success',
  'agent.run.failed': 'error',
  'agent.run.cancelled': 'warning',
  // Sub-agent events
  'agent.run.subagent_invoked': 'purple',
  'agent.run.subagent_returned': 'purple',
  'agent.run.subagent_started': 'info',
  'agent.run.subagent_completed': 'success',
  'agent.run.subagents_all_returned': 'success',
  // Knowledge Base events
  'kb.document.added': 'info',
  'kb.search-content.added': 'purple',
  'kb.processing.completed': 'success',
  // Email Inbound events
  'email.processing.started': 'info',
  'email.processing.completed': 'success',
  'email.processing.failed': 'error',
  // Connection Email entity events
  'entity.conn-email.created': 'gray',
  'entity.conn-email.updated': 'gray',
  'entity.conn-email.deleted': 'warning',
  // Surface lifecycle events
  'agent.run.surface_deleted': 'warning',
  // Integration events
  'integration.event.failed': 'error',
};

export function getEventBadgeVariant(eventType: string): BadgeVariant {
  if (eventBadgeMap[eventType]) return eventBadgeMap[eventType];
  if (eventType.startsWith('com.')) return 'info';
  if (eventType.endsWith('.completed')) return 'success';
  if (eventType.endsWith('.failed')) return 'error';
  return 'gray';
}

function CollapsibleContent({ content }: { content: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const isJson = isJsonLike(content);
  const needsExpand = !isJson && (content.length > 120 || content.includes('\n'));

  return (
    <div className="mt-1">
      {isJson ? (
        <p className="truncate rounded bg-muted/50 p-2 text-xs font-mono text-muted-foreground">{content}</p>
      ) : (
        <div className={`rounded bg-muted/50 p-2 ${open ? 'max-h-64 overflow-auto' : 'overflow-hidden'}`}>
          {open ? (
            <MarkdownContent>{content}</MarkdownContent>
          ) : (
            <div className="[&_*]:!my-0 [&_*]:!leading-tight"><MarkdownContent>{content.split('\n')[0]}</MarkdownContent></div>
          )}
        </div>
      )}
      {needsExpand && (
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-0.5">
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export function RunCompletedRenderer({ data }: { data: RunCompletedData }) {
  const preview = data.response?.length > 150
    ? data.response.slice(0, 150) + '…'
    : data.response;
  const duration = data.durationMs != null
    ? `${(data.durationMs / 1000).toFixed(1)}s`
    : null;
  const toolCount = (data.steps ?? []).filter((s) => s.type === 'tool_call').length;

  return (
    <div className="text-sm text-muted-foreground space-y-1">
      {preview && <p className="truncate">{preview}</p>}
      <p className="text-xs flex gap-3">
        {duration && <span>{duration}</span>}
        {toolCount > 0 && <span>{toolCount} tool call{toolCount !== 1 ? 's' : ''}</span>}
      </p>
    </div>
  );
}

export function RunFailedRenderer({ data }: { data: RunFailedData }) {
  return (
    <div className="text-sm space-y-1">
      <p className="text-red-600 dark:text-red-400">{data.error}</p>
      {data.code && (
        <p className="text-xs text-muted-foreground">Code: {data.code}</p>
      )}
    </div>
  );
}

export function RunCancelledRenderer({ data }: { data: Record<string, unknown> }) {
  const cancelledAt = data.cancelledAt as string | undefined;
  const cancelledBy = data.cancelledBy as string | undefined;
  return (
    <div className="text-sm text-yellow-600 dark:text-yellow-400">
      <p>Run cancelled{cancelledBy ? ` by ${cancelledBy}` : ''}</p>
      {cancelledAt && (
        <p className="text-xs text-muted-foreground">at {cancelledAt}</p>
      )}
    </div>
  );
}

export function RunStartedRenderer({ data }: { data: Record<string, unknown> }) {
  const subjectId = data.subjectId as string | undefined;
  return (
    <div className="text-sm text-muted-foreground">
      {subjectId ? <p>Subject: {subjectId}</p> : <p>Run started</p>}
    </div>
  );
}

export function RunDeltaRenderer({ data }: { data: RunReasoningDeltaData | RunResponseDeltaData }) {
  const delta = data.delta ?? '';
  const preview = delta.length > 100 ? delta.slice(0, 100) + '…' : delta;
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      {preview && <p className="truncate">{preview}</p>}
      <p className="text-xs">iteration: {data.iteration}</p>
    </div>
  );
}

export function RunToolCallRenderer({ data }: { data: RunToolCallData }) {
  const inputJson = Object.keys(data.input ?? {}).length > 0
    ? JSON.stringify(data.input)
    : '{}';
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p><span className="font-medium">{data.toolName}</span> <span className="text-xs opacity-70">{data.toolCallId}</span></p>
      <CollapsibleContent content={inputJson} label="input" />
      <p className="text-xs">iteration: {data.iteration}</p>
    </div>
  );
}

export function RunToolResultRenderer({ data }: { data: RunToolResultData }) {
  const outputContent = data.error
    ? data.error
    : data.output != null
      ? String(typeof data.output === 'string' ? data.output : JSON.stringify(data.output))
      : null;
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p>
        <span className="font-medium">{data.toolName}</span>{' '}
        <span className={`text-xs ${data.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {data.success ? 'success' : 'failed'}
        </span>
      </p>
      {outputContent && <CollapsibleContent content={outputContent} label={data.error ? 'error' : 'output'} />}
      <p className="text-xs">iteration: {data.iteration}</p>
    </div>
  );
}

export function RunResponseStopRenderer({ data }: { data: RunResponseStopData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p className="text-xs flex gap-3">
        <span>iteration: {data.iteration}</span>
        {data.isFinal != null && (
          <span className={data.isFinal ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}>
            {data.isFinal ? 'final response' : 'narration'}
          </span>
        )}
      </p>
      {data.content && <CollapsibleContent content={data.content} />}
    </div>
  );
}

export function RunFinalAnswerStopRenderer({ data }: { data: RunFinalAnswerStopData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p className="text-xs flex gap-3">
        <span>iteration: {data.iteration}</span>
        {data.usage && (
          <span>{data.usage.inputTokens.toLocaleString()} in / {data.usage.outputTokens.toLocaleString()} out</span>
        )}
      </p>
      {data.content && <CollapsibleContent content={data.content} label="final answer" />}
    </div>
  );
}

// ================================================================
// Sub-agent renderers
// ================================================================

function formatDurationSec(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

export function SubagentInvokedRenderer({ data }: { data: RunSubagentInvokedData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p className="text-xs flex gap-3">
        <span>{data.count} task{data.count !== 1 ? 's' : ''}</span>
        <span>mode: {data.mode}</span>
        <span>iteration: {data.iteration}</span>
      </p>
      <p className="text-xs truncate">
        agents: {data.tasks.map((t) => t.agentId).join(', ')}
      </p>
    </div>
  );
}

export function SubagentReturnedRenderer({ data }: { data: RunSubagentReturnedData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p className="text-xs flex gap-3">
        <span>{data.count} task{data.count !== 1 ? 's' : ''}</span>
        <span className={data.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
          {data.success ? 'success' : 'failed'}
        </span>
        <span>{formatDurationSec(data.durationMs)}</span>
      </p>
      {data.error && <p className="text-xs text-red-600 dark:text-red-400">{data.error}</p>}
      <p className="text-xs truncate">
        runs: {data.tasks.map((t) => t.childRunId).join(', ')}
      </p>
    </div>
  );
}

export function SubagentStartedRenderer({ data }: { data: RunSubagentStartedData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p className="text-xs flex gap-3">
        <span className="font-medium">{data.agentName}</span>
        <span>{data.agentId}</span>
      </p>
      <p className="text-xs">parent: {data.parentRunId}</p>
    </div>
  );
}

export function SubagentCompletedRenderer({ data }: { data: RunSubagentCompletedData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p className="text-xs flex gap-3">
        <span className="font-medium">{data.agentName}</span>
        <span className={data.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
          {data.success ? 'success' : 'failed'}
        </span>
        <span>{formatDurationSec(data.durationMs)}</span>
        {data.toolCallCount > 0 && <span>{data.toolCallCount} tool call{data.toolCallCount !== 1 ? 's' : ''}</span>}
      </p>
      {data.usage && (
        <p className="text-xs">{data.usage.inputTokens.toLocaleString()} in / {data.usage.outputTokens.toLocaleString()} out</p>
      )}
      {data.error && <p className="text-xs text-red-600 dark:text-red-400">{data.error}</p>}
      {data.response && <CollapsibleContent content={data.response} />}
    </div>
  );
}

export function SubagentsAllReturnedRenderer({ data }: { data: RunSubagentsAllReturnedData }) {
  const total = data.successCount + data.failureCount;
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p className="text-xs flex gap-3">
        <span>{data.successCount}/{total} succeeded</span>
        {data.failureCount > 0 && (
          <span className="text-red-600 dark:text-red-400">{data.failureCount} failed</span>
        )}
        <span>{formatDurationSec(data.totalDurationMs)}</span>
      </p>
      {data.tasks && data.tasks.length > 0 && (
        <div className="text-xs space-y-0.5">
          {data.tasks.map((t) => (
            <div key={t.taskId} className="flex gap-2">
              <span className={t.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {t.success ? '✓' : '✗'}
              </span>
              <span className="font-medium">{t.agentName}</span>
              <span>{formatDurationSec(t.durationMs)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// Knowledge Base renderers
// ================================================================

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function KBDocumentAddedRenderer({ data }: { data: KBDocumentAddedData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p><span className="font-medium">{data.fileName}</span> <span className="text-xs opacity-70">{data.docId}</span></p>
      <p className="text-xs flex gap-3">
        <span>type: {data.docType}</span>
        {data.sizeBytes != null && <span>{formatBytes(data.sizeBytes)}</span>}
        <span>status: {data.processingStatus}</span>
      </p>
    </div>
  );
}

export function KBSearchContentAddedRenderer({ data }: { data: KBSearchContentAddedData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p className="text-xs opacity-70">{data.docId}</p>
      <p className="text-xs flex gap-3">
        <span>{data.chunkCount} chunk{data.chunkCount !== 1 ? 's' : ''}</span>
        <span>dim: {data.vectorDimension}</span>
        <span>model: {data.model}</span>
      </p>
    </div>
  );
}

export function KBProcessingCompletedRenderer({ data }: { data: KBProcessingCompletedData }) {
  const duration = data.processingDurationMs != null
    ? `${(data.processingDurationMs / 1000).toFixed(1)}s`
    : null;
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p className="text-xs opacity-70">{data.docId}</p>
      <p className="text-xs flex gap-3">
        <span className={data.searchable ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}>
          {data.searchable ? 'searchable' : 'not searchable'}
        </span>
        {duration && <span>{duration}</span>}
        <span>{data.derivativesGenerated} derivative{data.derivativesGenerated !== 1 ? 's' : ''}</span>
      </p>
    </div>
  );
}

// ================================================================
// Email Inbound renderers
// ================================================================

export function EmailProcessingStartedRenderer({ data }: { data: EmailProcessingStartedData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p><span className="font-medium">{data.subject}</span></p>
      <p className="text-xs flex gap-3">
        <span>from: {data.sender}</span>
        <span>to: {data.emailAddress}</span>
        {data.attachmentCount > 0 && <span>{data.attachmentCount} attachment{data.attachmentCount !== 1 ? 's' : ''}</span>}
      </p>
    </div>
  );
}

export function EmailProcessingCompletedRenderer({ data }: { data: EmailProcessingCompletedData }) {
  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <p><span className="font-medium">{data.subject}</span></p>
      <p className="text-xs flex gap-3">
        <span>from: {data.sender}</span>
        {data.docIds?.length > 0 && <span>{data.docIds.length} doc{data.docIds.length !== 1 ? 's' : ''} created</span>}
        {data.urls?.length > 0 && <span>{data.urls.length} URL{data.urls.length !== 1 ? 's' : ''}</span>}
        {data.attachmentCount > 0 && <span>{data.attachmentCount} attachment{data.attachmentCount !== 1 ? 's' : ''}</span>}
      </p>
    </div>
  );
}

export function EmailProcessingFailedRenderer({ data }: { data: EmailProcessingFailedData }) {
  return (
    <div className="text-sm space-y-1">
      <p className="text-red-600 dark:text-red-400">{data.error}</p>
      <p className="text-xs text-muted-foreground flex gap-3">
        <span>from: {data.sender}</span>
        <span>to: {data.emailAddress}</span>
        {data.reason && <span>reason: {data.reason}</span>}
      </p>
    </div>
  );
}

// ================================================================
// Integration events (optr.conn.integrations)
// ================================================================

export interface IntegrationSuccessData {
  integrationId: string;
  provider: string;
  integrationEvent: {
    id?: string;
    type: string;
    time?: string;
    subject?: string;
    source?: string;
    data: {
      event?: string;
      action?: string;
      repository?: { fullName?: string; url?: string; name?: string };
      actor?: { login?: string; avatarUrl?: string; url?: string };
      title?: string;
      number?: number;
      state?: string;
      url?: string;
      body?: string;
      [k: string]: unknown;
    };
  };
}

export interface IntegrationFailureData {
  integrationId: string;
  provider: string;
  providerEventType: string;
  deliveryId?: string;
  stage: 'parse' | 'adapter-missing' | 'pipeline';
  errorMessage: string;
}

export function IntegrationEventRenderer({ data }: { data: IntegrationSuccessData }) {
  const inner = data.integrationEvent?.data ?? {};
  const repo = inner.repository?.fullName;
  const actor = inner.actor?.login;
  const avatar = inner.actor?.avatarUrl;

  return (
    <div className="text-sm text-muted-foreground space-y-1">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        {avatar && (
          <img src={avatar} alt="" className="h-4 w-4 rounded-full" />
        )}
        {actor && <span className="font-medium text-foreground">{actor}</span>}
        {inner.action && <span>{inner.action}</span>}
        {repo && (
          <span className="font-mono rounded bg-muted px-1.5 py-0.5">{repo}</span>
        )}
        {inner.number != null && <span>#{inner.number}</span>}
        {inner.state && (
          <span
            className={
              inner.state === 'open'
                ? 'text-green-600 dark:text-green-400'
                : inner.state === 'closed'
                  ? 'text-red-600 dark:text-red-400'
                  : ''
            }
          >
            {inner.state}
          </span>
        )}
      </div>
      {inner.title && <p className="text-foreground truncate">{cleanDisplayText(inner.title)}</p>}
      {inner.url && (
        <a
          href={String(inner.url)}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {String(inner.url)}
        </a>
      )}
    </div>
  );
}

export function IntegrationFailureRenderer({ data }: { data: IntegrationFailureData }) {
  return (
    <div className="text-sm space-y-1">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-1.5 py-0.5 font-medium">
          {data.stage}
        </span>
        <span className="text-muted-foreground">{data.provider}</span>
        {data.providerEventType && data.providerEventType !== 'unknown' && (
          <span className="font-mono text-muted-foreground">{data.providerEventType}</span>
        )}
      </div>
      <p className="text-red-600 dark:text-red-400">{data.errorMessage}</p>
      {data.deliveryId && (
        <p className="text-xs text-muted-foreground">delivery: {data.deliveryId}</p>
      )}
    </div>
  );
}

export function SurfaceDeletedRenderer({ data }: { data: { surfaceId: string; canvasId: string; success: boolean; error?: string; durationMs?: number } }) {
  return (
    <div className="text-sm space-y-1">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-muted-foreground">surface: {data.surfaceId}</span>
        <span className="text-muted-foreground">canvas: {data.canvasId}</span>
      </div>
      {!data.success && data.error && (
        <p className="text-red-600 dark:text-red-400">{data.error}</p>
      )}
      {data.durationMs != null && (
        <p className="text-xs text-muted-foreground">{data.durationMs}ms</p>
      )}
    </div>
  );
}

export function DefaultRenderer({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).slice(0, 6);
  if (entries.length === 0) return null;

  return (
    <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
      {entries.map(([key, value]) => (
        <span key={key} className="text-xs">
          <span className="text-muted-foreground font-medium">{key}</span>{' '}
          <span className="text-muted-foreground/70">{typeof value === 'string' ? value.slice(0, 80) : JSON.stringify(value)}</span>
        </span>
      ))}
    </div>
  );
}

export function getEventPreviewText(event: { eventType: string; data: Record<string, unknown> }): string {
  const { eventType, data } = event;
  switch (eventType) {
    case 'agent.run.completed': {
      const d = data as unknown as RunCompletedData;
      const dur = d.durationMs != null ? `${(d.durationMs / 1000).toFixed(1)}s` : '';
      return dur ? `completed in ${dur}` : 'completed';
    }
    case 'agent.run.failed': {
      const d = data as unknown as RunFailedData;
      const snippet = d.error?.length > 60 ? d.error.slice(0, 60) + '...' : d.error;
      return `failed: ${snippet}`;
    }
    case 'agent.run.cancelled':
      return 'cancelled';
    case 'agent.run.tool_call': {
      const d = data as unknown as RunToolCallData;
      return `tool_call: ${d.toolDisplayName || d.toolName}`;
    }
    case 'agent.run.tool_result': {
      const d = data as unknown as RunToolResultData;
      return `tool_result: ${d.toolName} (${d.success ? 'ok' : 'error'})`;
    }
    case 'agent.run.subagent_started': {
      const d = data as unknown as RunSubagentStartedData;
      return `subagent started: ${d.agentName}`;
    }
    case 'agent.run.subagent_completed': {
      const d = data as unknown as RunSubagentCompletedData;
      return `subagent completed: ${d.agentName}`;
    }
    case 'agent.run.subagents_all_returned': {
      const d = data as unknown as RunSubagentsAllReturnedData;
      const total = d.successCount + d.failureCount;
      return `${d.successCount}/${total} succeeded`;
    }
    case 'integration.event.failed': {
      const d = data as unknown as IntegrationFailureData;
      const snippet = d.errorMessage?.length > 60 ? d.errorMessage.slice(0, 60) + '…' : d.errorMessage;
      return `failed (${d.stage}): ${snippet ?? ''}`;
    }
    case 'agent.run.surface_deleted': {
      const d = data as { surfaceId?: string; success?: boolean; error?: string };
      if (!d.success) return `surface delete failed: ${d.error ?? 'unknown'}`;
      return `surface deleted: ${d.surfaceId ?? ''}`;
    }
    default: {
      // com.<provider>.<event> — integration events
      if (eventType.startsWith('com.') && (data as unknown as IntegrationSuccessData)?.integrationEvent) {
        const d = data as unknown as IntegrationSuccessData;
        const inner = d.integrationEvent?.data ?? {};
        const parts = [
          inner.action,
          inner.repository?.fullName,
          inner.number != null ? `#${inner.number}` : null,
          inner.title,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(' · ') : eventType;
      }
      // Strip 'agent.run.' prefix for a short label
      const short = eventType.startsWith('agent.run.')
        ? eventType.slice('agent.run.'.length)
        : eventType;
      return short;
    }
  }
}

export function renderEventData(eventType: string, data: Record<string, unknown>) {
  switch (eventType) {
    case 'agent.run.completed':
      return <RunCompletedRenderer data={data as unknown as RunCompletedData} />;
    case 'agent.run.failed':
      return <RunFailedRenderer data={data as unknown as RunFailedData} />;
    case 'agent.run.cancelled':
      return <RunCancelledRenderer data={data} />;
    case 'agent.run.started':
      return <RunStartedRenderer data={data} />;
    case 'agent.run.reasoning_delta':
    case 'agent.run.response_delta':
    case 'agent.run.final_answer_delta':
      return <RunDeltaRenderer data={data as unknown as RunReasoningDeltaData | RunResponseDeltaData | RunFinalAnswerDeltaData} />;
    case 'agent.run.response_stop':
      return <RunResponseStopRenderer data={data as unknown as RunResponseStopData} />;
    case 'agent.run.final_answer_stop':
      return <RunFinalAnswerStopRenderer data={data as unknown as RunFinalAnswerStopData} />;
    case 'agent.run.tool_call':
      return <RunToolCallRenderer data={data as unknown as RunToolCallData} />;
    case 'agent.run.tool_result':
      return <RunToolResultRenderer data={data as unknown as RunToolResultData} />;
    // Sub-agent events
    case 'agent.run.subagent_invoked':
      return <SubagentInvokedRenderer data={data as unknown as RunSubagentInvokedData} />;
    case 'agent.run.subagent_returned':
      return <SubagentReturnedRenderer data={data as unknown as RunSubagentReturnedData} />;
    case 'agent.run.subagent_started':
      return <SubagentStartedRenderer data={data as unknown as RunSubagentStartedData} />;
    case 'agent.run.subagent_completed':
      return <SubagentCompletedRenderer data={data as unknown as RunSubagentCompletedData} />;
    case 'agent.run.subagents_all_returned':
      return <SubagentsAllReturnedRenderer data={data as unknown as RunSubagentsAllReturnedData} />;
    // Knowledge Base events
    case 'kb.document.added':
      return <KBDocumentAddedRenderer data={data as unknown as KBDocumentAddedData} />;
    case 'kb.search-content.added':
      return <KBSearchContentAddedRenderer data={data as unknown as KBSearchContentAddedData} />;
    case 'kb.processing.completed':
      return <KBProcessingCompletedRenderer data={data as unknown as KBProcessingCompletedData} />;
    // Email Inbound events
    case 'email.processing.started':
      return <EmailProcessingStartedRenderer data={data as unknown as EmailProcessingStartedData} />;
    case 'email.processing.completed':
      return <EmailProcessingCompletedRenderer data={data as unknown as EmailProcessingCompletedData} />;
    case 'email.processing.failed':
      return <EmailProcessingFailedRenderer data={data as unknown as EmailProcessingFailedData} />;
    // Surface lifecycle events
    case 'agent.run.surface_deleted':
      return <SurfaceDeletedRenderer data={data as unknown as { surfaceId: string; canvasId: string; success: boolean; error?: string; durationMs?: number }} />;
    // Integration events
    case 'integration.event.failed':
      return <IntegrationFailureRenderer data={data as unknown as IntegrationFailureData} />;
    default:
      if (eventType.startsWith('com.') && (data as unknown as IntegrationSuccessData)?.integrationEvent) {
        return <IntegrationEventRenderer data={data as unknown as IntegrationSuccessData} />;
      }
      return <DefaultRenderer data={data} />;
  }
}
